// src/pages/item/useItemScreen.ts
import { useDispatch, useSelector } from "react-redux";
import { useMemo, useState, useEffect } from "react";
import {
  AvailabilityResult,
  CartItem,
  SaleItemInterface,
  StockStore,
} from "../../common/types/types";
import { selectCategoryList } from "../../store/slices/categoryListSlice";
import { useItemInteractions } from "../../common/hooks/useItemInteractions";
import {
  addToCart,
  decreaseQuantity,
  increaseQuantity,
  removeFromCart,
} from "../../store/slices/cartSlice";
import { useRatingModal } from "../../common/components/ratingModal/useRatingModal";
import { selectPurchasedItemsMap } from "../../store/slices/orderSlice";
import { RootState } from "../../store/store";
import {
  selectSaleItem,
  upsertItems,
} from "../../store/slices/saleItemListSlice";
import { selectCurrentUser } from "../../store/slices/currentUserSlice";
import { selectCurrentCluster } from "../../store/slices/currentClusterSlice";
import { selectForceStores } from "../../store/slices/storeListSlice";
import { checkItemAvailability } from "../../common/utils/functions/checkItemAvailability";
import { AvailabilityStatus } from "../../common/types/enums";
import { useCartActions } from "../../common/hooks/useCartActions";
import { useDisplayPrice } from "../../common/hooks/useDisplayPrice";
import { getApi } from "../../api/api";

export const useItemScreen = (itemId: number) => {
  const dispatch = useDispatch();
  const categoryList = useSelector(selectCategoryList);
  const { toggleFavorite } = useItemInteractions();
  const { openModal } = useRatingModal();

  const itemFromState = useSelector((state: RootState) =>
    selectSaleItem(state, itemId),
  );
  const currentUser = useSelector(selectCurrentUser);
  const purchasedMap = useSelector(selectPurchasedItemsMap);
  const currentCluster = useSelector(selectCurrentCluster);
  const selectedStoreIds = useSelector(selectForceStores);

  const [loadedItem, setLoadedItem] = useState<SaleItemInterface | null>(null);
  const [isLoading, setIsLoading] = useState(false);

  const getPrice = useDisplayPrice();

  useEffect(() => {
    if (!itemFromState && itemId) {
      const loadItem = async () => {
        setIsLoading(true);
        try {
          const api = getApi();
          const response = await api.getItemById(itemId);
          if (response) {
            setLoadedItem(response);
            dispatch(upsertItems([response]));
          }
        } catch (error) {
          console.error("Failed to load item:", error);
        } finally {
          setIsLoading(false);
        }
      };
      loadItem();
    }
  }, [itemId, itemFromState, dispatch]);

  const item = itemFromState || loadedItem;

  const cartItem = useSelector((state: RootState) =>
    state.cart.items.find(
      (el: CartItem) => !el.groupId && String(el.id) === String(itemId),
    ),
  );

  const quantity = cartItem?.quantity || 0;
  const hasPurchasedItem = purchasedMap.get(itemId) || false;

  const {
    isComplexCategory,
    showComplexButton,
    showCartButton,
    handleAddToComplex,
  } = useCartActions(item || ({} as SaleItemInterface));

  const maxAvailable = useMemo(() => {
    if (!item?.useStock) return Infinity;
    return (
      item?.stock_stores?.reduce(
        (s: number, i: StockStore) => s + (i.quantity - i.reserved),
        0,
      ) || 0
    );
  }, [item]);

  const isIncreaseDisabled = useMemo(() => {
    if (!item?.useStock) return false;
    return quantity >= maxAvailable;
  }, [quantity, maxAvailable, item?.useStock]);

  const availability: AvailabilityResult = useMemo(() => {
    if (!item) {
      return {
        available: false,
        status: AvailabilityStatus.NO_STORE_SELECTED,
      };
    }
    return checkItemAvailability({
      item: item,
      forceStore: currentCluster?.forceStore ?? false,
      selectedStoreIds,
    });
  }, [item, currentCluster?.forceStore, selectedStoreIds]);

  const isOutOfStock = !availability.available;

  const hasUserRated =
    item?.ratings?.some((rating) => rating.userId === currentUser?.id) || false;

  const canRateItem = hasPurchasedItem && !hasUserRated;

  const handleFavoritePress = async () => {
    if (item?.selected === undefined || !item) return;
    toggleFavorite(item.id!);
  };

  const handleAddToCart = () => {
    if (!item) return;
    const price = getPrice(item.prices);
    dispatch(
      addToCart({
        id: String(item.id),
        name: item.name,
        price,
        image: item.files?.[0],
        categoryId: Number(item.categoryId),
        minQuantity: item.minQuantity ?? 1,
        quantityStep: item.quantityStep ?? 1,
        attributes: item.attributes || [],
        unit: item.unit
      }),
    );
  };

  const handleIncrease = () => {
    if (!item) return;
    if (!isIncreaseDisabled) {
      dispatch(increaseQuantity(String(item.id)));
    }
  };

  const handleDecrease = () => {
    if (!item) return;
    dispatch(decreaseQuantity(String(item.id)));
  };

  const handleRemove = () => {
    if (!item) return;
    dispatch(removeFromCart(String(item.id)));
  };

  const getCategoryName = (id: number) => {
    return categoryList.find((el) => el.id === id)?.name;
  };

  if (!item && !isLoading) {
    return {
      currentItem: null,
      getCategoryName,
      openModal,
      handleFavoritePress: () => {},
      handleIncrease: () => {},
      handleDecrease: () => {},
      handleRemove: () => {},
      canRateItem: false,
      handleAddToCart: () => {},
      handleAddToComplex: () => {},
      quantity: 0,
      maxAvailable: 0,
      isIncreaseDisabled: true,
      isOutOfStock: true,
      isLoading: false,
      availability: {
        available: false,
        status: AvailabilityStatus.NO_STORE_SELECTED,
      },
      isComplexCategory: false,
      showComplexButton: false,
      showCartButton: true,
    };
  }

  return {
    getCategoryName,
    openModal,
    handleFavoritePress,
    handleIncrease,
    handleDecrease,
    handleRemove,
    canRateItem,
    handleAddToCart,
    handleAddToComplex,
    quantity,
    currentItem: item,
    maxAvailable,
    isIncreaseDisabled,
    isOutOfStock,
    isLoading,
    availability,
    isComplexCategory,
    showComplexButton,
    showCartButton,
  };
};
